"use client";

import { GoogleGenAI, type LiveServerMessage, type Session } from "@google/genai";
import { GEMINI_TRANSCRIPTION_CONFIG, GEMINI_TRANSCRIPTION_MODEL } from "@/lib/gemini-live-transcription-config";

type Unsubscribe = () => void;

/** One live dictation, as the composer's voice seat drives it. */
export declare namespace DictationAdapter {
  type Status = { type: "starting" | "running" } | { type: "ended"; reason: "stopped" | "cancelled" | "error" };
  type Result = { transcript: string; isFinal?: boolean };
  type Session = {
    status: Status;
    stop: () => Promise<void>;
    cancel: () => void;
    onSpeechStart: (callback: () => void) => Unsubscribe;
    onSpeechEnd: (callback: (result: Result) => void) => Unsubscribe;
    onSpeech: (callback: (result: Result) => void) => Unsubscribe;
  };
}

type ResultCallback = (result: DictationAdapter.Result) => void;
type TokenResponse = { token: string; model: string };

const pcm16Base64 = (samples: Float32Array): string => {
  const bytes = new Uint8Array(samples.length * 2);
  const view = new DataView(bytes.buffer);
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index] ?? 0));
    view.setInt16(index * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]!);
  return btoa(binary);
};

export class GeminiLiveDictationAdapter {
  listen(): DictationAdapter.Session {
    const speechStart = new Set<() => void>();
    const speechEnd = new Set<ResultCallback>();
    const speech = new Set<ResultCallback>();
    const abort = new AbortController();
    let live: Session | undefined;
    let microphone: MediaStream | undefined;
    let audioContext: AudioContext | undefined;
    let source: MediaStreamAudioSourceNode | undefined;
    let capture: AudioWorkletNode | undefined;
    let cancelled = false;
    let stopping = false;
    let stopResolve: (() => void) | undefined;
    let stopTimer: ReturnType<typeof setTimeout> | undefined;
    let lastResult: DictationAdapter.Result = { transcript: "", isFinal: true };

    const session: DictationAdapter.Session = {
      status: { type: "starting" },
      stop: async () => {
        if (session.status.type === "ended") return;
        stopping = true;
        stopCapture();
        await ready.catch(() => undefined);
        if (!live) return;
        live.sendRealtimeInput({ activityEnd: {} });
        await new Promise<void>((resolve) => {
          stopResolve = resolve;
          stopTimer = setTimeout(resolve, 3_000);
        });
        finish("stopped");
      },
      cancel: () => {
        cancelled = true;
        abort.abort();
        finish("cancelled");
      },
      onSpeechStart: (callback) => {
        speechStart.add(callback);
        return () => { speechStart.delete(callback); };
      },
      onSpeechEnd: (callback) => {
        speechEnd.add(callback);
        return () => { speechEnd.delete(callback); };
      },
      onSpeech: (callback) => {
        speech.add(callback);
        return () => { speech.delete(callback); };
      },
    };

    const stopCapture = () => {
      source?.disconnect();
      capture?.disconnect();
      microphone?.getTracks().forEach(track => track.stop());
      microphone = undefined;
      if (audioContext && audioContext.state !== "closed") void audioContext.close();
      audioContext = undefined;
    };

    const finish = (reason: "stopped" | "cancelled" | "error") => {
      if (session.status.type === "ended") return;
      if (stopTimer) clearTimeout(stopTimer);
      stopResolve?.();
      stopResolve = undefined;
      stopCapture();
      live?.close();
      live = undefined;
      session.status = { type: "ended", reason };
      if (reason !== "cancelled") for (const callback of speechEnd) callback(lastResult);
    };

    const onMessage = (message: LiveServerMessage) => {
      const content = message.serverContent;
      const interim = content?.interimInputTranscription?.text;
      if (interim !== undefined) {
        const result = { transcript: interim, isFinal: false };
        lastResult = result;
        for (const callback of speech) callback(result);
      }
      const transcript = content?.inputTranscription?.text;
      if (transcript !== undefined) {
        const result = { transcript, isFinal: true };
        lastResult = result;
        for (const callback of speech) callback(result);
        if (stopping) stopResolve?.();
      }
    };

    const ready = (async () => {
      try {
        const response = await fetch("/api/transcription/token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: abort.signal,
        });
        if (!response.ok) throw new Error(`transcription token request failed (${response.status})`);
        const credentials = await response.json() as TokenResponse;
        if (!credentials.token || credentials.model !== GEMINI_TRANSCRIPTION_MODEL) throw new Error("invalid transcription token response");

        microphone = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        });
        if (cancelled) return;
        audioContext = new AudioContext();
        await audioContext.audioWorklet.addModule("/orik-pcm-capture-worklet.js");
        source = audioContext.createMediaStreamSource(microphone);
        capture = new AudioWorkletNode(audioContext, "orik-pcm-capture", {
          numberOfInputs: 1,
          numberOfOutputs: 0,
          channelCount: 1,
        });

        const client = new GoogleGenAI({ apiKey: credentials.token });
        live = await client.live.connect({
          model: credentials.model,
          config: GEMINI_TRANSCRIPTION_CONFIG,
          callbacks: {
            onmessage: onMessage,
            onerror: (event) => {
              console.error("Voice input error", event.message);
              finish("error");
            },
            onclose: () => {
              if (!stopping && !cancelled) finish("error");
            },
          },
        });
        if (cancelled) { finish("cancelled"); return; }

        capture.port.onmessage = (event: MessageEvent<Float32Array>) => {
          if (!live || stopping || cancelled) return;
          live.sendRealtimeInput({
            audio: { data: pcm16Base64(event.data), mimeType: "audio/pcm;rate=16000" },
          });
        };
        live.sendRealtimeInput({ activityStart: {} });
        source.connect(capture);
        await audioContext.resume();
        session.status = { type: "running" };
        for (const callback of speechStart) callback();
      } catch (error) {
        if (!cancelled) console.error("Unable to start voice input", error);
        finish(cancelled ? "cancelled" : "error");
        throw error;
      }
    })();
    void ready.catch(() => undefined);

    return session;
  }
}
